import { MonetizationType } from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { ModalHelper } from '@/helpers/vfm/modal-helper';

import { TitleDetail } from '@/interfaces/title-details-graphql';
import { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import { S72ModalContexts } from '@/features/jwtv-titles-rental/tracking';

const jwtvTitleRentalModal = () => import('@/features/jwtv-titles-rental/components/JwtvTitleRentalModal.vue');

type JWTVModalProps = (
	| {
			title: TitleDetail;
			offer: TitleOfferFragment | WatchNowOfferFragment;
			overrideSuccessUrl?: string | null;
			promoCode?: string;
			titleSlug?: string;
			isFallback?: boolean;
	  }
	| {
			finalUrl: string;
	  }
) & {
	additionalContexts?: S72ModalContexts;
};

export async function openJwTvRentalModal(payload: { modalProps: JWTVModalProps; onClose?: () => void }) {
	await ModalHelper.openModal(
		jwtvTitleRentalModal,
		{
			...payload.modalProps,
		},
		{
			cssClass: 'account-modal',
			id: 'jwtv-rent-modal',
		},
		undefined,
		undefined,
		payload.modalProps.additionalContexts,
		() => {
			payload.onClose?.();
		}
	);
}

export function getJWTVTitleUrl(options: {
	fullPath: string;
	linkContextId?: string;
	expCode?: string;
	isFallback?: boolean;
	fullUrl?: boolean;
}): string | null {
	const { fullPath, linkContextId, expCode, isFallback, fullUrl } = options;

	if (!fullPath) {
		return null;
	}
	// For local development, we need to use the localhost domain
	const params = new URLSearchParams({ package: 'jwt' });
	if (linkContextId) params.set('lcId', linkContextId);
	if (expCode) params.set('expPC', expCode);
	if (isFallback) params.set('isFb', '1');
	const queryParams = `?${params.toString()}`;
	let baseUrl = `https://www.${JW_CONFIG.DOMAIN}`;
	if (process.client && window.location.hostname === 'localhost') {
		baseUrl = `http://localhost:8080`;
	}
	if (fullUrl) {
		return `${baseUrl}${fullPath}${queryParams}`;
	}

	return `${fullPath}${queryParams}`;
}

const queryParamMapping = {
	lcId: 'linkContextId',
	expPC: 'expPromoCode',
	play: 'titleSlug',
	isFb: 'isFallback',
} as const;

export function getJWTVQueryParams(routerQuery: Record<string, string>) {
	const params: {
		packageShortName?: string;
		linkContextId?: string;
		expPromoCode?: string;
		titleSlug?: string;
		isFallback?: boolean;
	} = {};

	Object.entries(queryParamMapping).forEach(([queryKey, paramKey]) => {
		if (routerQuery[queryKey]) {
			if (paramKey === 'isFallback') {
				params[paramKey] = routerQuery[queryKey] === '1';
			} else {
				params[paramKey] = routerQuery[queryKey];
			}
		}
	});

	if (routerQuery['package'] === 'jwt') {
		params['packageShortName'] = 'jwt';
	}

	return params;
}

export function isJWTVRentalOffer(offer: TitleOfferFragment | WatchNowOfferFragment): boolean {
	const isJustWatchTvPackage = offer?.package.technicalName === 'justwatchtv';
	const isBuyOrRent = [MonetizationType.Buy, MonetizationType.Rent].includes(offer.monetizationType);
	const isTvStreamUrl = !!offer.streamUrl?.includes('tv.justwatch.com');

	return isJustWatchTvPackage && (isBuyOrRent || isTvStreamUrl);
}
